import React, { AnimationEvent, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, BoxProps, TextField } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWithActionBar from '../FormWithActionBar/FormWithActionBar';
import { ControlsStringAssets, MessagesStringAssets } from '../../../assets/stringAssets';
import { ISocialMediaPost } from '../../../dataObjects/models/digitalMedia/SocialMediaPost';
import { ISaveSocialMediaPostRequest } from '../../../dataObjects/models/serviceRequests/socialMediaPost/SaveSocialMediaPostRequest';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';
import { styled } from '@mui/styles';
import { SocialMediaPostDisplay } from '../../controls/digitalMediaDisplays/SocialMediaPostDisplay';
import { enumDigitalMediaDisplayEnvironment } from '../../../dataObjects/enums';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area to display the Preview Container
const StyledBoxForPreviewContainer = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
}));

// a styled Box (equivalent to a <div>), providing an area to display the Preview Item
const StyledBoxForPreviewItemArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  objectFit: 'contain',  // 'contain' & 'scale-down' seem to work similarly
}));

interface ISocialMediaPostFormValues {
  url: string;
  description: string;
}

// using 'yup', set up a schema for the form field values
const schema = yup.object().shape({
  url: yup
    .string()
    .matches(RegularExpressions.GenericUrl, ControlsStringAssets.socialMediaPostUrlInvalid)
    // .url({message: ControlsStringAssets.socialMediaPostUrlInvalid, regex: RegularExpressions.GenericUrl})
    .required(ControlsStringAssets.socialMediaPostUrlRequired),
  description: yup
    .string()
    .required(ControlsStringAssets.socialMediaPostDescriptionRequired),
});

export interface ISocialMediaPostFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {ISocialMediaPost} socialMediaPost The SocialMediaPost details for the form (will have blank properties values if we're creating a new record)
   */
  socialMediaPost: ISocialMediaPost,
  /**
   * @property {boolean} saveRequestInProgress Whether a save request is in progress
   */
  saveRequestInProgress?: boolean,
  /**
   * @property {(socialMediaPost: ISocialMediaPost) => Promise<void>} onSubmit Method to call for submitting the form for a save operation
   */
  onSubmit: (socialMediaPost: ISaveSocialMediaPostRequest) => Promise<void>,
}

const SocialMediaPostForm: React.FC<ISocialMediaPostFormProps> = (props: ISocialMediaPostFormProps) => {
  SocialMediaPostForm.displayName = 'SocialMediaPost Form';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = true;

  // get required arguments from props
  const { socialMediaPost, onSubmit } = props;

  // set up details for ReactHookForm
  const { formState, formState: { errors }, getValues, handleSubmit, register } = useForm<ISocialMediaPostFormValues>({
    defaultValues: {
      url: socialMediaPost.url,
      description: socialMediaPost.description
    },
    // mode: "onBlur",
    mode: "all",
    resolver: yupResolver(schema)
  });

  const { ref: urlReg, ...urlProps } = register("url", { required: true });
  const { ref: descriptionReg, ...descriptionProps } = register("description", { required: true, minLength: 1 });

  // for capturing the current value of the Social Media Post Url field  
  const [currentSocialMediaPostUrlValue, setCurrentSocialMediaPostUrlValue] = useState<string>(socialMediaPost.url);

  // for capturing the current value of the Description field
  const [currentDescription, setCurrentDescription] = useState<string>(socialMediaPost.description);

  const [isDescriptionAutoFilled, setIsDescriptionAutoFilled] = useState<boolean>(false);
  const [isSocialMediaPostUrlAutoFilled, setIsSocialMediaPostUrlAutoFilled] = useState<boolean>(false);

  const [formIsValid, setFormIsValid] = useState<boolean>(false);

  // determines whether the description field value is valid
  const validDescriptionFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentDescription !== undefined) {
        isFieldValueValid = currentDescription.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isDescriptionAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentDescription, isDescriptionAutoFilled]
  );

  // determines whether the social media post url field value is valid
  const validSocialMediaPostUrlFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentSocialMediaPostUrlValue !== undefined) {
        isFieldValueValid = RegularExpressions.GenericUrl.test(currentSocialMediaPostUrlValue);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isSocialMediaPostUrlAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentSocialMediaPostUrlValue, isSocialMediaPostUrlAutoFilled]
  );

  const allRequiredFieldValuesValid: () => boolean = useCallback(
    () => {
      let fieldValuesValid: boolean = false;

      displayConsoleLogs && console.log(`%c SocialMediaPostForm. In allRequiredFieldValuesValid(); currentSocialMediaPostUrlValue: ${currentSocialMediaPostUrlValue}; currentDescription: ${currentDescription}`, 'background: #055; color: #fff');

      // if the social media post url and the description are both valid...
      if (validSocialMediaPostUrlFieldValue() && validDescriptionFieldValue()) {
        fieldValuesValid = true;
      }

      return fieldValuesValid;
    },
    [validSocialMediaPostUrlFieldValue, validDescriptionFieldValue]
  );

  const onDescriptionAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onDescriptionAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onDescriptionAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isDescriptionAutoFilled) {
            setIsDescriptionAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onDescriptionAnimationStart(); getValues('description'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  const onSocialMediaPostUrlAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onSocialMediaPostUrlAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onSocialMediaPostUrlAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isSocialMediaPostUrlAutoFilled) {
            setIsSocialMediaPostUrlAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c SocialMediaPostForm. In onSocialMediaPostUrlAnimationStart(); getValues('socialMediaPostUrl'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  useEffect(() => {
    // let isFormValid: boolean = isValid && allRequiredFieldValuesValid();
    let isFormValid: boolean = allRequiredFieldValuesValid();

    displayConsoleLogs && console.log(`%c SocialMediaPostForm. useEffect for [allRequiredFieldValuesValid]; isFormValid: ${isFormValid}`, 'background: #055; color: #fff');

    setFormIsValid(isFormValid);
  }, [allRequiredFieldValuesValid]);

  // capture whether a save is currently being submitted
  const saveRequestInProgress: boolean = props.saveRequestInProgress ?? false;

  // state value indicating whether a save is in progress
  const [saveInProgress, setSaveInProgress] = useState<boolean>(saveRequestInProgress);

  // useEffect hook for setting the 'saveInProgress' local state based on whether a save is currently in progress
  useEffect(() => {
    setSaveInProgress(saveRequestInProgress);
  }, [saveRequestInProgress]);

  // handles a save/submit request from the form
  const handleSaveSubmit = async (data: ISocialMediaPostFormValues) => {

    setSaveInProgress(true);

    // fill in title & description of the SocialMediaPost object passed in
    socialMediaPost.url = data.url;
    socialMediaPost.description = data.description;

    // prepare a request for saving, starting with just the videoLink
    let saveSocialMediaPostRequest: ISaveSocialMediaPostRequest = {
      socialMediaPost: socialMediaPost
    };

    // call the onSubmit handler passed in, supplying the SocialMediaPost object
    await onSubmit(saveSocialMediaPostRequest);
  }

  // present the form
  return (
    <>
      <FormWithActionBar
        onSubmit={handleSubmit(handleSaveSubmit)}
        actionInProgress={saveInProgress}
        actionInProgressLabel={MessagesStringAssets.socialMediaPost_SaveRequested}
        formIsValid={formIsValid}
      >

        <TextField
          inputRef={urlReg}
          {...urlProps}
          label={ControlsStringAssets.socialMediaPostUrlLabel}
          margin='normal'
          fullWidth
          multiline={true}
          minRows={3}
          maxRows={5}
          error={!!errors.url}
          helperText={errors?.url?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onSocialMediaPostUrlAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentSocialMediaPostUrlValue(e.target.value);
          }} />

        <StyledBoxForPreviewContainer>
          <StyledBoxForPreviewItemArea>
            <SocialMediaPostDisplay socialMediaPost={{ ...socialMediaPost, url: currentSocialMediaPostUrlValue, description: currentDescription }}
              displayEnvironment={enumDigitalMediaDisplayEnvironment.FormView}
            />

          </StyledBoxForPreviewItemArea>
        </StyledBoxForPreviewContainer>

        <TextField
          inputRef={descriptionReg}
          {...descriptionProps}
          label={ControlsStringAssets.socialMediaPostDescriptionLabel}
          margin='normal'
          fullWidth
          multiline={true}
          minRows={3}
          maxRows={5}
          error={!!errors.description}
          helperText={errors?.description?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onDescriptionAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentDescription(e.target.value);
          }}
        />

      </FormWithActionBar>
    </>

  );
}

export default SocialMediaPostForm;